import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import {
    BundleIssue,
    BundleKind,
    BundleReport,
    downloadBundleTemplate,
    importBundleFile,
} from '../api/bundle';
import { listProcessArchs, ProcessArch, archUsable } from '../api/parameter';
import { listMaterialCategories } from '../api/materialCategory';
import { ApiError } from '../api/request';
import SearchSelect from './SearchSelect';

interface BundleImportModalProps {
    open: boolean;
    onClose: () => void;
    /** 套件类型: device = 设备+模式+能力+校准+参数; material = 材料+SKU+能力位+理化+贴士+参数 */
    kind: BundleKind;
    /** 提交成功 (直执行或已建工单) 后回调, 父组件刷新列表. */
    onImported?: () => void;
}

// severity → 行样式 (顶部汇总条与逐条列表共用).
const SEV_STYLE: Record<string, { badge: string; row: string; icon: string }> =
    {
        error: {
            badge: 'bg-rose-100 text-rose-700',
            row: 'border-rose-200 bg-rose-50',
            icon: '✖',
        },
        warning: {
            badge: 'bg-amber-100 text-amber-700',
            row: 'border-amber-200 bg-amber-50',
            icon: '⚠',
        },
        info: {
            badge: 'bg-slate-100 text-slate-500',
            row: 'border-slate-200 bg-slate-50',
            icon: 'ℹ',
        },
    };

const KIND_TEXT: Record<
    BundleKind,
    { title: string; scopeLabel: string; sections: string }
> = {
    device: {
        title: '设备套件导入 (设备+模式+能力+校准+参数 一次创建)',
        scopeLabel: '工艺框架',
        sections:
            '一台设备的身份、支持的加工模式、能力字段、功率校准曲线与参数行',
    },
    material: {
        title: '材料套件导入 (材料+SKU+能力位+理化+贴士+参数 一次创建)',
        scopeLabel: '一级类别',
        sections: '一个材料的主数据、SKU、能力位、理化参数、小贴士挂载与参数行',
    },
};

// 单条预检结果: 行号 + 路径 + 文案 + 候选 chip (点击复制) + fixAt 跳转.
const IssueRow = ({ issue }: { issue: BundleIssue }) => {
    const st = SEV_STYLE[issue.severity] || SEV_STYLE.info;
    return (
        <div className={`border rounded px-3 py-2 text-xs space-y-1 ${st.row}`}>
            <div className="flex items-start gap-2">
                <span>{st.icon}</span>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        {issue.line ? (
                            <span className="font-mono text-[11px] px-1 rounded bg-white/70">
                                第 {issue.line} 行
                            </span>
                        ) : null}
                        {issue.path && (
                            <span className="font-mono text-[11px] text-slate-500 break-all">
                                {issue.path}
                            </span>
                        )}
                        <span
                            className={`text-[10px] px-1 rounded font-mono ${st.badge}`}
                        >
                            {issue.code}
                        </span>
                    </div>
                    <div className="mt-0.5">{issue.msg}</div>
                    {issue.candidates?.length ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                            <span className="text-slate-400">是否想写:</span>
                            {issue.candidates.slice(0, 8).map((cd) => (
                                <button
                                    key={cd}
                                    type="button"
                                    title="点击复制"
                                    className="font-mono text-[11px] px-1.5 rounded border border-slate-200 bg-white hover:bg-brand-50 hover:border-brand-300"
                                    onClick={() =>
                                        navigator.clipboard?.writeText(cd)
                                    }
                                >
                                    {cd}
                                </button>
                            ))}
                            {issue.candidates.length > 8 && (
                                <span className="text-slate-400">
                                    … 共 {issue.candidates.length} 个 (详见
                                    preflight)
                                </span>
                            )}
                        </div>
                    ) : null}
                    {issue.fixAt && (
                        <div className="mt-1">
                            <Link
                                to={issue.fixAt}
                                target="_blank"
                                className="text-brand-600 hover:underline"
                            >
                                去补前置数据 → {issue.fixAt}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * 套件文件导入 (JSON/YAML), 设备与材料共用:
 * 使用说明+模板下载 → 上传自动预检 → 告警确认 → 提交 (一个审批工单).
 * 交互骨架对齐 XlsxImportModal (模板→上传→校验→提交), 结果按五要素结构化展示.
 */
const BundleImportModal = ({
    open,
    onClose,
    kind,
    onImported,
}: BundleImportModalProps) => {
    const text = KIND_TEXT[kind];
    // 模板作用域选项: 设备=工艺框架, 材料=一级类别.
    const [scopeOptions, setScopeOptions] = useState<
        { value: string; label: string }[]
    >([]);
    const [scope, setScope] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [report, setReport] = useState<BundleReport | null>(null);
    const [checking, setChecking] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [ack, setAck] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState<{
        taskCode?: string;
        created?: number;
    } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    useEffect(() => {
        if (!open) {
            setFile(null);
            setReport(null);
            setAck(false);
            setError('');
            setDone(null);
            setDragOver(false);
            return;
        }
        const load =
            kind === 'device'
                ? listProcessArchs().then((list: ProcessArch[]) =>
                      list
                          .filter(archUsable)
                          .map((a) => ({
                              value: a.code,
                              label: a.label || a.code,
                          })),
                  )
                : listMaterialCategories().then((tree) =>
                      tree.map((c) => ({
                          value: c.categoryCode,
                          label: `${c.nameZh || c.nameEn}`,
                      })),
                  );
        load.then((opts) => {
            setScopeOptions(opts);
            setScope((cur) => cur || (opts[0]?.value ?? ''));
        }).catch(() => setScopeOptions([]));
    }, [open, kind]);

    // 选文件即自动预检 (dryRun), 一次报全.
    const handleFile = async (f: File) => {
        setFile(f);
        setReport(null);
        setAck(false);
        setError('');
        setDone(null);
        setChecking(true);
        try {
            const { data } = await importBundleFile(kind, f, { dryRun: true });
            setReport(data.report);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setChecking(false);
        }
    };

    const canSubmit = useMemo(() => {
        if (!file || !report || checking || submitting || done) return false;
        if (!report.ok) return false;
        if (report.warnings.length > 0 && !ack) return false;
        return true;
    }, [file, report, checking, submitting, ack, done]);

    const onSubmit = async () => {
        if (!file) return;
        setSubmitting(true);
        setError('');
        try {
            const { data, message } = await importBundleFile(kind, file, {
                ackWarnings: ack,
            });
            setReport(data.report);
            if (data.accepted === false) {
                setError(message || '提交被拒绝, 请按报告修改后重试');
                return;
            }
            setDone({ taskCode: data.taskCode, created: data.created });
            if (onImported) onImported();
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setSubmitting(false);
        }
    };

    const dl = (format: 'yaml' | 'json', mode: 'blank' | 'example') => {
        if (!scope) return;
        const scopeParam: Record<string, string> =
            kind === 'device' ? { archCode: scope } : { type1: scope };
        downloadBundleTemplate(kind, scopeParam, format, mode).catch((e) =>
            setError(
                `模板下载失败: ${e instanceof ApiError ? e.message : String(e)}`,
            ),
        );
    };

    return (
        <Modal open={open} onClose={onClose} title={text.title} width={860}>
            <div className="space-y-4">
                {/* ① 使用说明 + 模板下载: 三层信息各自成行 (说明 / 模板下载 / 三步引导),
                    下载按钮按 YAML·JSON 分两组固定成两行, 避免宽度一变就断在奇怪的位置 */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="text-xs text-slate-600 leading-relaxed">
                        一份 JSON / YAML 文件描述{text.sections}
                        。上传后自动预检,
                        <b>任一处错误整体拒绝</b>;提交生成 <b>一个审批工单</b>,
                        通过后单事务落库(参数行随工单一并审批, 通过即发布)。
                    </div>

                    <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                        <div className="shrink-0 pt-1.5 text-xs font-medium text-slate-600">
                            下载模板
                        </div>
                        <div
                            className="w-32 shrink-0"
                            title={`模板按${text.scopeLabel}实时生成`}
                        >
                            <SearchSelect
                                className="w-full"
                                value={scope}
                                onChange={(v) => setScope(v)}
                                options={scopeOptions}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex flex-wrap gap-1.5">
                                <span className="w-10 shrink-0 pt-1 text-[11px] text-slate-400">
                                    YAML
                                </span>
                                <button
                                    type="button"
                                    className="btn-ghost text-xs whitespace-nowrap"
                                    onClick={() => dl('yaml', 'example')}
                                >
                                    ⬇ 完整示例
                                </button>
                                <button
                                    type="button"
                                    className="btn-ghost text-xs whitespace-nowrap"
                                    onClick={() => dl('yaml', 'blank')}
                                >
                                    ⬇ 空白模板
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                <span className="w-10 shrink-0 pt-1 text-[11px] text-slate-400">
                                    JSON
                                </span>
                                <button
                                    type="button"
                                    className="btn-ghost text-xs whitespace-nowrap"
                                    onClick={() => dl('json', 'example')}
                                >
                                    ⬇ 完整示例
                                </button>
                                <button
                                    type="button"
                                    className="btn-ghost text-xs whitespace-nowrap"
                                    onClick={() => dl('json', 'blank')}
                                >
                                    ⬇ 空白模板
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="text-[11px] text-slate-400">
                        模板按所选{text.scopeLabel}实时生成,
                        字段与校验同源;头部有逐段填写说明, 字段注释里带候选值
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 border-t border-slate-200 pt-2.5">
                        {[
                            '下载示例',
                            '改成自己的数据',
                            '上传预检 (有错逐条改, 全绿再提交)',
                        ].map((s, i) => (
                            <span key={s} className="flex items-center gap-1.5">
                                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 text-[10px] text-slate-600">
                                    {i + 1}
                                </span>
                                {s}
                            </span>
                        ))}
                    </div>
                </div>

                {/* ② 上传区 */}
                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        const f = e.dataTransfer.files?.[0];
                        if (f) handleFile(f);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-5 text-center text-sm cursor-pointer transition-colors ${
                        dragOver
                            ? 'border-brand-500 bg-brand-50 text-brand-700'
                            : 'border-slate-200 text-slate-400 hover:border-slate-300'
                    }`}
                >
                    {file ? (
                        <div className="space-y-1">
                            <div className="text-slate-700 font-mono text-xs">
                                {file.name}{' '}
                                <span className="text-slate-400">
                                    ({(file.size / 1024).toFixed(1)} KB)
                                </span>
                            </div>
                            <div className="text-[11px] text-brand-600">
                                点击或拖拽其它文件替换 (替换后自动重新预检)
                            </div>
                        </div>
                    ) : (
                        <>
                            拖拽 .json / .yaml 套件文件到此 或{' '}
                            <span className="text-brand-600">点击上传</span>
                        </>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json,.yaml,.yml,application/json,text/yaml"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleFile(f);
                            e.target.value = '';
                        }}
                    />
                </div>

                {/* ③ 预检报告 */}
                {checking && (
                    <div className="text-sm text-slate-500">预检中…</div>
                )}
                {report && (
                    <div className="space-y-2">
                        {/* 顶部汇总条: 第一眼可见 */}
                        {report.ok ? (
                            <div className="rounded px-3 py-2 text-sm font-semibold cap-on">
                                ✔ 预检通过 · {report.summary.bundles || 1}{' '}
                                条套件
                                {report.warnings.length > 0 &&
                                    ` · ${report.warnings.length} 项告警需确认`}
                            </div>
                        ) : (
                            <div className="rounded px-3 py-2 text-sm font-semibold bg-rose-100 text-rose-700">
                                ✖ 预检未通过 · {report.errors.length} 项错误 ·{' '}
                                {report.warnings.length} 项告警
                            </div>
                        )}
                        <div className="space-y-1.5 max-h-72 overflow-auto pr-1">
                            {report.errors.map((it, i) => (
                                <IssueRow key={`e${i}`} issue={it} />
                            ))}
                            {report.warnings.map((it, i) => (
                                <IssueRow key={`w${i}`} issue={it} />
                            ))}
                            {report.infos.map((it, i) => (
                                <IssueRow key={`i${i}`} issue={it} />
                            ))}
                        </div>
                        {/* D10: 有告警必须勾选确认 (服务端同样校验 ackWarnings) */}
                        {report.ok && report.warnings.length > 0 && !done && (
                            <label className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={ack}
                                    onChange={(e) => setAck(e.target.checked)}
                                />
                                我已逐条知晓以上 {report.warnings.length}{' '}
                                项告警, 确认按原值提交
                                (告警将随工单呈现给审批人)
                            </label>
                        )}
                    </div>
                )}

                {/* ④ 结果 / 错误 */}
                {done && (
                    <div className="rounded px-3 py-2 text-sm cap-on">
                        {done.taskCode
                            ? `已提交审批工单 ${done.taskCode}, 待管理员通过后生效 (审批中心可跟踪)`
                            : `套件已创建 (${done.created || 1} 条)`}
                    </div>
                )}
                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-ghost"
                        disabled={submitting}
                    >
                        {done ? '关闭' : '取消'}
                    </button>
                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={!canSubmit}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? '提交中…' : '提交 (一个审批工单)'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default BundleImportModal;
